import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import ModalConfirmar from './ModalConfirmar';
import type { OperadorLoja } from '../types';

interface Props {
  eventoId: number;
  aoSalvo: () => void;
}

// Lista de operadores do evento, como cartões-linha com avatar e lixeira.
export default function PainelOperadores({ eventoId, aoSalvo }: Props) {
  const [operadores, setOperadores] = useState<OperadorLoja[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [removendo, setRemovendo] = useState<OperadorLoja | null>(null);

  async function carregar() {
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('loja_operadores_do_evento', {
        p_evento: eventoId,
      });
      if (error) throw error;
      const lista = ((data as {
        id: number;
        ponto_id: number | null;
        nome: string;
        email: string;
        caixa: boolean;
      }[]) || []).map((o) => ({
        operador_id: o.id,
        ponto_id: o.ponto_id,
        ponto_nome: null,
        nome: o.nome,
        email: o.email,
        ja_entrou: true,
        caixa: o.caixa,
      }));
      setOperadores(lista);
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  async function confirmarRemover() {
    if (!removendo) return;
    try {
      const { error } = await supabase.rpc('loja_remover_operador', {
        p_operador: removendo.operador_id,
      });
      if (error) throw error;
      setRemovendo(null);
      setCarregando(true);
      await carregar();
      aoSalvo();
    } catch (e) {
      setErro(mensagemAmigavel(e));
      setRemovendo(null);
    }
  }

  return (
    <div className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <div className="flex items-center justify-between gap-3 px-1">
        <h2 className="text-[17px] font-bold text-foreground-950">Operadores</h2>
      </div>

      {erro && <p className="mt-2 px-1 text-[14px] text-danger-600">{erro}</p>}

      {carregando ? (
        <div className="mt-3 space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-2xl bg-secondary-100" />
          ))}
        </div>
      ) : operadores.length === 0 ? (
        <p className="mt-3 px-1 text-[14px] text-foreground-500">
          Nenhum operador convidado ainda. Convide alguém para atender as barracas e o caixa.
        </p>
      ) : (
        <ul className="mt-2">
          {operadores.map((o) => (
            <li key={o.operador_id} className="flex min-h-[56px] items-center gap-3 py-2">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[14px] font-bold text-primary-700">
                {o.nome.charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-medium text-foreground-950">{o.nome}</p>
                <p className="truncate text-[13px] text-foreground-500">{o.email}</p>
              </div>
              {o.caixa && (
                <span className="shrink-0 rounded-full bg-primary-100 px-2.5 py-1 text-[12px] font-semibold text-primary-700">
                  Caixa
                </span>
              )}
              <button
                type="button"
                onClick={() => setRemovendo(o)}
                aria-label={`Remover ${o.nome}`}
                className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl text-foreground-400 transition-colors hover:bg-danger-50 hover:text-danger-600"
              >
                <i className="ri-delete-bin-line text-lg" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {removendo && (
        <ModalConfirmar
          titulo="Remover operador"
          mensagem={
            <>
              Remover <strong className="font-semibold">{removendo.nome}</strong>? Ela deixa de
              atender as barracas e o caixa deste evento.
            </>
          }
          rotuloConfirmar="Remover"
          aoConfirmar={confirmarRemover}
          aoCancelar={() => setRemovendo(null)}
        />
      )}
    </div>
  );
}